/* eslint-disable no-unused-vars */
import React from 'react';

const SQL_SCHEMA_SCRIPT = `-- 1. สร้างตารางเก็บโปรไฟล์ผู้ใช้งาน
CREATE TABLE IF NOT EXISTS public.user_profiles (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  email text UNIQUE NOT NULL,
  name text,
  first_name text,
  last_name text,
  is_activated boolean DEFAULT false,
  activated_at timestamp with time zone,
  role text DEFAULT 'อื่นๆ' NOT NULL,
  allowed_pages text DEFAULT '' NOT NULL,
  created_at timestamp with time zone DEFAULT timezone('utc'::text, now()) NOT NULL,
  last_login timestamp with time zone DEFAULT timezone('utc'::text, now()) NOT NULL
);

-- 2. สร้างตารางเก็บค่าสิทธิ์เริ่มต้นของแต่ละบทบาท
CREATE TABLE IF NOT EXISTS public.role_default_pages (
  role text PRIMARY KEY,
  allowed_pages text DEFAULT '' NOT NULL
);

-- 3. บันทึกสิทธิ์เริ่มต้นของแต่ละบทบาท
INSERT INTO public.role_default_pages (role, allowed_pages)
VALUES
  ('admin', 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace,recall,dental-risk'),
  ('ทันตแพทย์', 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace,recall'),
  ('ทันตาภิบาล', 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace,recall'),
  ('ผู้ช่วยทันตแพทย์', 'washing,packaging,supply,dispense,recall'),
  ('อื่นๆ', 'clinic-order,barcode-tracking,trace')
ON CONFLICT (role) DO UPDATE SET allowed_pages = EXCLUDED.allowed_pages;

-- 4. ตั้งค่า RLS (Row Level Security) - แนะนำให้รันไฟล์ scripts/security_hardening_rls.sql เพื่อเปิดการใช้งานและควบคุมสิทธิ์อย่างปลอดภัย
-- ดูรายละเอียดใน scripts/security_hardening_rls.sql`;

const ManageUsersPage = ({
  isAdmin = true,
  dbError = '',
  defaultSaveStatus = '',
  roleOptions = ['admin', 'ทันตแพทย์', 'ทันตาภิบาล', 'ผู้ช่วยทันตแพทย์', 'อื่นๆ'],
  selectedDefaultRoleTab = 'admin',
  setSelectedDefaultRoleTab,
  PAGES_LIST = [],
  roleDefaults = {},
  handleUpdateRoleDefaultPages,
  newUserEmail = '',
  setNewUserEmail,
  newUserName = '',
  setNewUserName,
  newUserFirstName = '',
  setNewUserFirstName,
  newUserLastName = '',
  setNewUserLastName,
  newUserRole = 'อื่นๆ',
  setNewUserRole,
  handleCreateUser,
  handleAddUserProfile,
  userSearchQuery = '',
  setUserSearchQuery,
  userProfiles = [],
  getUserNameByEmail,
  handleUpdateUserRole,
  handleUpdateRole,
  handleToggleUserPageAccess,
  handleUpdateAllowedPages,
  handleResetUserToRoleDefault,
  setResetPasswordUser,
  setNewPasswordValue,
  setDeleteConfirmUser,
  handleDeleteUserProfile,
  setSqlScriptToShow,
  sqlScriptToShow = false,
  user = null,
  handleUpdateNameLocal,
  handleUpdateNameDb,
  filteredProfiles: propFilteredProfiles
}) => {
  // Local state for first_name and last_name inputs (synced with newUserName)
  const [firstNameInput, setFirstNameInput] = React.useState(newUserFirstName || '');
  const [lastNameInput, setLastNameInput] = React.useState(newUserLastName || '');

  React.useEffect(() => {
    if (newUserFirstName) setFirstNameInput(newUserFirstName);
    if (newUserLastName) setLastNameInput(newUserLastName);
    if (!newUserName) {
      setFirstNameInput('');
      setLastNameInput('');
    } else if (!newUserFirstName && !newUserLastName) {
      const parts = newUserName.trim().split(/\s+/);
      setFirstNameInput(parts[0] || '');
      setLastNameInput(parts.slice(1).join(' ') || '');
    }
  }, [newUserName, newUserFirstName, newUserLastName]);

  if (!isAdmin) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: '#ef4444', background: '#fff', borderRadius: '16px' }}>
        ⚠️ ขออภัย คุณไม่มีสิทธิ์เข้าถึงหน้านี้ (เฉพาะผู้ดูแลระบบ Admin เท่านั้น)
      </div>
    );
  }

  // Unified add user handler
  const onSubmitAddUser = (e) => {
    if (handleCreateUser) return handleCreateUser(e);
    if (handleAddUserProfile) return handleAddUserProfile(e);
    e.preventDefault();
  };

  // Unified role update handler
  const onUpdateRole = (email, newRole) => {
    if (handleUpdateUserRole) return handleUpdateUserRole(email, newRole);
    if (handleUpdateRole) return handleUpdateRole(email, newRole);
  };

  // Unified allowed pages toggle handler
  const onTogglePageAccess = (email, pageId, currentAllowedList, isChecked) => {
    if (handleToggleUserPageAccess) {
      return handleToggleUserPageAccess(email, pageId);
    }
    let nextAllowed;
    if (isChecked) {
      nextAllowed = [...currentAllowedList, pageId];
    } else {
      nextAllowed = currentAllowedList.filter((id) => id !== pageId);
    }
    const nextStr = nextAllowed.join(',');
    if (handleUpdateAllowedPages) return handleUpdateAllowedPages(email, nextStr);
  };

  // Reset user permissions to role default
  const onResetUserToDefault = (email, role) => {
    if (handleResetUserToRoleDefault) return handleResetUserToRoleDefault(email, role);
    const defaultPages = roleDefaults[role] || '';
    if (handleUpdateAllowedPages) return handleUpdateAllowedPages(email, defaultPages);
  };

  // Unified delete handler
  const onDeleteUser = (profile) => {
    if (handleDeleteUserProfile) return handleDeleteUserProfile(profile);
    if (setDeleteConfirmUser) {
      if (user?.email && profile.email?.toLowerCase() === user.email.toLowerCase()) {
        return alert('คุณไม่สามารถลบตัวเองได้');
      }
      setDeleteConfirmUser(profile);
    }
  };

  // Unified reset password click handler
  const onResetPasswordClick = (profile) => {
    if (setResetPasswordUser) setResetPasswordUser(profile);
    if (setNewPasswordValue) setNewPasswordValue('');
    if (setSqlScriptToShow) setSqlScriptToShow(false);
  };

  // Name resolver helper
  const getUserName = getUserNameByEmail || ((email) => (email ? email.split('@')[0] : ''));

  // Filtered profiles list
  const effectiveFilteredProfiles =
    propFilteredProfiles ||
    (userProfiles || []).filter(
      (p) =>
        (p.email || '').toLowerCase().includes((userSearchQuery || '').toLowerCase()) ||
        (p.name || '').toLowerCase().includes((userSearchQuery || '').toLowerCase()) ||
        (p.role || '').toLowerCase().includes((userSearchQuery || '').toLowerCase())
    );

  return (
    <div
      style={{
        background: '#fff',
        padding: '30px',
        borderRadius: '16px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.05)',
        border: '1px solid #eef2f6',
        marginBottom: '30px'
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <span style={{ fontSize: '3.5em', display: 'block', marginBottom: '10px' }}>👤</span>
        <h2 style={{ color: '#2c3e50', margin: '0 0 5px 0', fontSize: '1.8em', fontWeight: 'bold' }}>
          จัดการผู้ใช้งานและสิทธิ์เข้าใช้
        </h2>
        <p style={{ color: '#7f8c8d', margin: 0, fontSize: '0.95em' }}>
          กำหนดบทบาท หน้าที่ และพิกัดสิทธิ์ของพนักงานในระบบ CSSD (จำกัด 1 บทบาทต่อ 1 ผู้ใช้)
        </p>
      </div>

      {/* ⚠️ Warning card if database tables are missing or requested */}
      {(dbError === 'table_not_found' || sqlScriptToShow) && (
        <div
          style={{
            background: 'linear-gradient(135deg, #fffdf6 0%, #fff9e6 100%)',
            border: '1px solid #ffeeba',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '25px',
            color: '#856404',
            textAlign: 'left'
          }}
        >
          <div
            style={{
              fontWeight: 'bold',
              fontSize: '1.1em',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}
          >
            <span>
              ⚠️{' '}
              {dbError === 'table_not_found'
                ? 'ระบบทำงานในโหมด "ฐานข้อมูลจำลอง (Local Storage)"'
                : 'สคริปต์ SQL ติดตั้งตารางและสิทธิ์เริ่มต้น'}
            </span>
            {setSqlScriptToShow && (
              <button
                type="button"
                onClick={() => setSqlScriptToShow(false)}
                style={{
                  background: 'none',
                  border: '1px solid #d97706',
                  color: '#b45309',
                  borderRadius: '6px',
                  padding: '4px 10px',
                  fontSize: '0.8em',
                  cursor: 'pointer'
                }}
              >
                ✕ ปิดหน้าต่างนี้
              </button>
            )}
          </div>
          <p style={{ fontSize: '0.9em', margin: '0 0 15px 0', lineHeight: '1.6' }}>
            {dbError === 'table_not_found' ? (
              <>
                เนื่องจากไม่พบตาราง <code>user_profiles</code> หรือ <code>role_default_pages</code> ในฐานข้อมูล
                Supabase ข้อมูลการจัดการสิทธิ์จะถูกบันทึกชั่วคราวบนเว็บเบราว์เซอร์นี้เท่านั้น
                หากต้องการเชื่อมต่อกับฐานข้อมูล Supabase อย่างสมบูรณ์ กรุณานำสคริปต์ SQL ด้านล่างนี้ไปรันใน{' '}
                <strong>Supabase SQL Editor</strong>:
              </>
            ) : (
              <>
                นำสคริปต์ SQL ด้านล่างนี้ไปรันใน <strong>Supabase SQL Editor</strong> เพื่อสร้างตารางและสิทธิ์เริ่มต้น:
              </>
            )}
          </p>
          <div style={{ position: 'relative' }}>
            <pre
              style={{
                background: '#1e293b',
                color: '#f8fafc',
                padding: '15px',
                borderRadius: '8px',
                overflowX: 'auto',
                fontSize: '0.82em',
                lineHeight: '1.5',
                margin: '0 0 15px 0',
                fontFamily: 'Courier New, monospace'
              }}
            >
              {SQL_SCHEMA_SCRIPT}
            </pre>
            <button
              type="button"
              onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(SQL_SCHEMA_SCRIPT);
                  alert('คัดลอก SQL Script เรียบร้อยแล้ว!');
                }
              }}
              style={{
                position: 'absolute',
                right: '10px',
                top: '10px',
                padding: '6px 12px',
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.8em',
                fontWeight: 'bold'
              }}
            >
              📋 คัดลอก SQL
            </button>
          </div>
        </div>
      )}

      {/* ⚙️ Card: ตั้งค่าค่าเริ่มต้นบทบาท */}
      <div
        style={{
          background: '#f8fafc',
          padding: '24px',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          marginBottom: '30px',
          textAlign: 'left'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '15px',
            marginBottom: '20px'
          }}
        >
          <div>
            <h3 style={{ margin: '0 0 5px 0', color: '#1e293b', fontSize: '1.2em', fontWeight: 'bold' }}>
              ⚙️ ตั้งค่าสิทธิ์เข้าใช้งานมาตรฐานตามบทบาท (Default Role Permissions)
            </h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.85em' }}>
              กำหนดว่าเวลาสร้างผู้ใช้ใหม่ตามบทบาทนั้นๆ จะได้รับสิทธิ์ในการดูหน้าไหนเป็นค่าเริ่มต้น
            </p>
          </div>
          {defaultSaveStatus && (
            <span
              style={{
                padding: '6px 12px',
                background: '#dcfce7',
                color: '#166534',
                borderRadius: '8px',
                fontSize: '0.85em',
                fontWeight: 'bold',
                animation: 'pulse 1.5s infinite'
              }}
            >
              {defaultSaveStatus}
            </span>
          )}
        </div>

        {/* Role Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '2px solid #e2e8f0',
            gap: '5px',
            marginBottom: '20px',
            overflowX: 'auto'
          }}
        >
          {roleOptions.map((role) => {
            const isActive = selectedDefaultRoleTab === role;
            return (
              <button
                key={role}
                type="button"
                onClick={() => setSelectedDefaultRoleTab && setSelectedDefaultRoleTab(role)}
                style={{
                  padding: '10px 18px',
                  border: 'none',
                  background: 'none',
                  borderBottom: isActive ? '3px solid #6366f1' : '3px solid transparent',
                  color: isActive ? '#6366f1' : '#64748b',
                  fontWeight: isActive ? 'bold' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '0.9em',
                  whiteSpace: 'nowrap'
                }}
              >
                {role === 'admin'
                  ? '👑 admin'
                  : role === 'ทันตแพทย์'
                    ? '🦷 ทันตแพทย์'
                    : role === 'ทันตาภิบาล'
                      ? '🩺 ทันตาภิบาล'
                      : role === 'ผู้ช่วยทันตแพทย์'
                        ? '🛠️ ผู้ช่วยทันตแพทย์'
                        : role}
              </button>
            );
          })}
        </div>

        {/* Checkbox settings list for active tab */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {PAGES_LIST.map((pg) => {
            const currentAllowedStr = roleDefaults[selectedDefaultRoleTab] || '';
            const currentAllowedList = currentAllowedStr ? currentAllowedStr.split(',') : [];
            const isChecked = currentAllowedList.includes(pg.id);

            const handleToggleCheckbox = () => {
              let updatedList;
              if (isChecked) {
                updatedList = currentAllowedList.filter((id) => id !== pg.id);
              } else {
                updatedList = [...currentAllowedList, pg.id];
              }
              if (handleUpdateRoleDefaultPages) {
                handleUpdateRoleDefaultPages(selectedDefaultRoleTab, updatedList.join(','));
              }
            };

            return (
              <label
                key={pg.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  background: isChecked ? '#eff6ff' : '#fff',
                  border: isChecked ? '1px solid #3b82f6' : '1px solid #cbd5e1',
                  borderRadius: '10px',
                  fontSize: '0.88em',
                  cursor: 'pointer',
                  color: isChecked ? '#1e40af' : '#475569',
                  transition: 'all 0.15s',
                  userSelect: 'none'
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={handleToggleCheckbox}
                  disabled={selectedDefaultRoleTab === 'admin'}
                  style={{ cursor: selectedDefaultRoleTab === 'admin' ? 'not-allowed' : 'pointer' }}
                />
                {pg.name}
              </label>
            );
          })}
        </div>
        {selectedDefaultRoleTab === 'admin' && (
          <div style={{ marginTop: '12px', fontSize: '0.8em', color: '#64748b', fontStyle: 'italic' }}>
            * บทบาทแอดมิน (admin) ถูกกำหนดให้สามารถเข้าใช้งานได้ทุกหน้าโดยอัตโนมัติและไม่สามารถปิดได้
          </div>
        )}
      </div>

      {/* ➕ Form: เพิ่มผู้ใช้งานใหม่ */}
      <div
        style={{
          background: '#f8fafc',
          padding: '24px',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          marginBottom: '30px',
          textAlign: 'left'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.2em', fontWeight: 'bold' }}>
            ➕ เพิ่มผู้ใช้งานใหม่เข้าระบบ
          </h3>
          <span style={{ fontSize: '0.82em', color: '#0369a1', background: '#e0f2fe', padding: '4px 10px', borderRadius: '8px' }}>
            💡 บัญชีใหม่จะรอการเปิดใช้งานครั้งแรก (Pending Activation)
          </span>
        </div>
        <form
          onSubmit={onSubmitAddUser}
          style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end' }}
        >
          <div style={{ flex: '2 1 240px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#475569' }}>
              อีเมลผู้ใช้งาน (Email)
            </label>
            <input
              type="text"
              placeholder="name@fangdent.com"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail && setNewUserEmail(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.95em',
                outline: 'none'
              }}
              required
            />
          </div>

          <div style={{ flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#475569' }}>
              ชื่อจริง (First Name)
            </label>
            <input
              type="text"
              placeholder="เช่น สมชาย"
              value={firstNameInput}
              onChange={(e) => {
                const val = e.target.value;
                setFirstNameInput(val);
                const full = `${val} ${lastNameInput}`.trim();
                if (setNewUserName) setNewUserName(full);
                if (setNewUserFirstName) setNewUserFirstName(val);
              }}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#475569' }}>
              นามสกุล (Last Name)
            </label>
            <input
              type="text"
              placeholder="เช่น ใจดี"
              value={lastNameInput}
              onChange={(e) => {
                const val = e.target.value;
                setLastNameInput(val);
                const full = `${firstNameInput} ${val}`.trim();
                if (setNewUserName) setNewUserName(full);
                if (setNewUserLastName) setNewUserLastName(val);
              }}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.95em',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#475569' }}>
              บทบาท / ตำแหน่ง (Role)
            </label>
            <select
              value={newUserRole}
              onChange={(e) => setNewUserRole && setNewUserRole(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.95em',
                background: '#fff',
                cursor: 'pointer',
                height: '42px'
              }}
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r === 'admin'
                    ? '👑 admin'
                    : r === 'ทันตแพทย์'
                      ? '🦷 ทันตแพทย์'
                      : r === 'ทันตาภิบาล'
                        ? '🩺 ทันตาภิบาล'
                        : r === 'ผู้ช่วยทันตแพทย์'
                          ? '🛠️ ผู้ช่วยทันตแพทย์'
                          : r}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            style={{
              padding: '10px 24px',
              background: '#22c55e',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95em',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)',
              height: '42px'
            }}
          >
            ➕ เพิ่มผู้ใช้งาน
          </button>
        </form>
        <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#64748b' }}>
          * เจ้าหน้าที่ใหม่สามารถเข้าสู่ระบบครั้งแรกโดยกรอก <strong>ชื่อ (Username)</strong> และ <strong>นามสกุล (Password)</strong> เพื่อเปิดใช้งานบัญชี
        </div>
      </div>

      {/* 📋 Table: รายชื่อผู้ใช้ทั้งหมด */}
      <div style={{ textAlign: 'left' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '15px',
            marginBottom: '15px'
          }}
        >
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.2em', fontWeight: 'bold' }}>
            📋 รายชื่อผู้ใช้และสิทธิ์ทั้งหมดในระบบ ({effectiveFilteredProfiles.length} บัญชี)
          </h3>
          <input
            type="text"
            placeholder="🔍 ค้นหาด้วยอีเมล หรือตำแหน่ง..."
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery && setUserSearchQuery(e.target.value)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '0.9em',
              width: '250px',
              outline: 'none'
            }}
          />
        </div>

        <div style={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: '12px', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92em' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 'bold', textAlign: 'left' }}>
                  👤 ชื่อผู้ใช้งาน / Login
                </th>
                <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 'bold', textAlign: 'left' }}>
                  👤 ชื่อแสดงจริง / Name
                </th>
                <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 'bold', textAlign: 'left' }}>
                  🚦 สถานะ
                </th>
                <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 'bold', textAlign: 'left' }}>
                  🔑 บทบาท / ตำแหน่ง
                </th>
                <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 'bold', textAlign: 'left' }}>
                  🖥️ สิทธิ์เข้าใช้หน้า
                </th>
                <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 'bold', textAlign: 'left' }}>
                  📅 วันที่ลงทะเบียน
                </th>
                <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 'bold', textAlign: 'left' }}>
                  🕒 ล็อกอินล่าสุด
                </th>
                <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 'bold', textAlign: 'center' }}>
                  ⚙️ การจัดการ
                </th>
              </tr>
            </thead>
            <tbody>
              {effectiveFilteredProfiles.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                    ไม่พบข้อมูลผู้ใช้งานตามเงื่อนไขที่ค้นหา
                  </td>
                </tr>
              ) : (
                effectiveFilteredProfiles.map((p, idx) => {
                  const isUserAdmin = p.role === 'admin';
                  const allowedPages = p.allowed_pages ? p.allowed_pages.split(',') : [];
                  const isSelf = user?.email && p.email?.toLowerCase() === user.email.toLowerCase();

                  return (
                    <tr
                      key={p.id || p.email}
                      style={{
                        borderBottom: '1px solid #e2e8f0',
                        transition: 'background 0.2s',
                        backgroundColor: idx % 2 === 0 ? '#fff' : '#fcfdfe'
                      }}
                    >
                      <td style={{ padding: '14px 18px', fontWeight: '500', color: '#1e293b' }}>
                        {p.email}
                        {isSelf && (
                          <span
                            style={{
                              marginLeft: '8px',
                              fontSize: '0.75em',
                              background: '#dbeafe',
                              color: '#1e40af',
                              padding: '3px 8px',
                              borderRadius: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            คุณ
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '14px 18px' }}>
                        <input
                          type="text"
                          value={p.name || ''}
                          placeholder={getUserName(p.email)}
                          onChange={(e) => {
                            if (handleUpdateNameLocal) handleUpdateNameLocal(p.email, e.target.value);
                          }}
                          onBlur={(e) => {
                            if (handleUpdateNameDb) handleUpdateNameDb(p.email, e.target.value);
                          }}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.9em',
                            width: '150px',
                            background: '#fff'
                          }}
                        />
                        {(p.first_name || p.last_name) && (
                          <div style={{ fontSize: '0.75em', color: '#64748b', marginTop: '3px' }}>
                            {p.first_name || '-'} {p.last_name || ''}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 18px', whiteSpace: 'nowrap' }}>
                        {p.is_activated === false ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              border: '1px solid #fde68a'
                            }}
                          >
                            🟡 รอเปิดใช้งาน (Pending)
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              backgroundColor: '#dcfce7',
                              color: '#166534',
                              border: '1px solid #bbf7d0'
                            }}
                          >
                            🟢 เปิดใช้งานแล้ว (Active)
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '14px 18px' }}>
                        <select
                          value={p.role || 'อื่นๆ'}
                          onChange={(e) => onUpdateRole(p.email, e.target.value)}
                          disabled={isSelf}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.9em',
                            background: '#fff',
                            cursor: isSelf ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {roleOptions.map((r) => (
                            <option key={r} value={r}>
                              {r === 'admin'
                                ? '👑 admin'
                                : r === 'ทันตแพทย์'
                                  ? '🦷 ทันตแพทย์'
                                  : r === 'ทันตาภิบาล'
                                    ? '🩺 ทันตาภิบาล'
                                    : r === 'ผู้ช่วยทันตแพทย์'
                                      ? '🛠️ ผู้ช่วยทันตแพทย์'
                                      : r}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '14px 18px' }}>
                        {isUserAdmin ? (
                          <div style={{ color: '#16a34a', fontWeight: 'bold', fontSize: '0.85em' }}>
                            ✨ เข้าถึงได้ทุกหน้า (แอดมิน)
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxWidth: '300px' }}>
                              {PAGES_LIST.map((pg) => {
                                const isPageAllowed = allowedPages.includes(pg.id);
                                return (
                                  <label
                                    key={pg.id}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '3px',
                                      fontSize: '0.78em',
                                      cursor: 'pointer',
                                      background: isPageAllowed ? '#f0fdf4' : '#f8fafc',
                                      border: isPageAllowed ? '1px solid #22c55e' : '1px solid #e2e8f0',
                                      padding: '3px 6px',
                                      borderRadius: '8px',
                                      color: isPageAllowed ? '#166534' : '#64748b',
                                      transition: 'all 0.15s',
                                      userSelect: 'none'
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isPageAllowed}
                                      onChange={(e) =>
                                        onTogglePageAccess(p.email, pg.id, allowedPages, e.target.checked)
                                      }
                                      style={{ cursor: 'pointer' }}
                                    />
                                    {pg.name}
                                  </label>
                                );
                              })}
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                type="button"
                                onClick={() => onResetUserToDefault(p.email, p.role)}
                                title="รีเซ็ตสิทธิ์กลับเป็นค่ามาตรฐานของบทบาท"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#6366f1',
                                  fontSize: '0.78em',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0
                                }}
                              >
                                🔄 รีเซ็ตเป็นค่าเริ่มต้นของบทบาท
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 18px', color: '#64748b', fontSize: '0.9em' }}>
                        {p.created_at
                          ? new Date(p.created_at).toLocaleDateString('th-TH') +
                            ' ' +
                            new Date(p.created_at).toLocaleTimeString('th-TH', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '-'}
                      </td>
                      <td style={{ padding: '14px 18px', color: '#64748b', fontSize: '0.9em' }}>
                        {p.last_login
                          ? new Date(p.last_login).toLocaleDateString('th-TH') +
                            ' ' +
                            new Date(p.last_login).toLocaleTimeString('th-TH', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '-'}
                      </td>
                      <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            type="button"
                            onClick={() => onResetPasswordClick(p)}
                            style={{
                              padding: '6px 12px',
                              background: 'none',
                              border: '1px solid #3b82f6',
                              color: '#3b82f6',
                              borderRadius: '6px',
                              fontSize: '0.85em',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#3b82f6';
                              e.target.style.color = '#fff';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#3b82f6';
                            }}
                          >
                            🔑 เปลี่ยนรหัสผ่าน
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteUser(p)}
                            disabled={isSelf}
                            style={{
                              padding: '6px 12px',
                              background: 'none',
                              border: '1px solid #ef4444',
                              color: '#ef4444',
                              borderRadius: '6px',
                              fontSize: '0.85em',
                              cursor: isSelf ? 'not-allowed' : 'pointer',
                              opacity: isSelf ? 0.4 : 1,
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelf) {
                                e.target.style.backgroundColor = '#ef4444';
                                e.target.style.color = '#fff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelf) {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#ef4444';
                              }
                            }}
                          >
                            🗑️ ลบ
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageUsersPage;
